"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type UserSettings = Record<string, unknown>;

export type User = {
  id: string;
  email: string;
  name?: string;
  point?: number;
  bio?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  settings?: UserSettings;
};

type AuthContextValue = {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, userName: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string>;
  refreshUser: () => Promise<void>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  authFetch: (path: string, init?: RequestInit) => Promise<any>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const API = (process.env.NEXT_PUBLIC_API_BASE_URL || "").replace(/\/+$/, "");

function joinUrl(path: string) {
  return `${API}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Đọc hạn dùng của access token để làm mới trước khi nó hết, không xác thực chữ ký. */
function readTokenExpiry(token: string): number | null {
  try {
    const raw = token.split(".")[1];
    if (!raw) return null;
    const base64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const exp = Number(JSON.parse(atob(padded))?.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

async function apiFetch(path: string, init?: RequestInit) {
  const res = await fetch(joinUrl(path), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    credentials: "include",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.code;
    err.data = data;
    throw err;
  }
  return data;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async (token: string) => {
    const res = await fetch(joinUrl("/auth/me"), {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err: any = new Error(data?.message || "Unauthorized");
      err.status = res.status;
      err.code = data?.code;
      err.data = data;
      throw err;
    }

    setUser((data.user as User) ?? null);
  }, []);

  const refresh = useCallback(async () => {
    const data = await apiFetch("/auth/refresh", { method: "POST" });
    const token: string | undefined = data?.accessToken;
    if (!token) throw new Error("Missing accessToken from /auth/refresh");

    setAccessToken(token);

    // Backend refresh đã trả user, ưu tiên dùng luôn
    if (data?.user) setUser(data.user as User);
    else await fetchMe(token);
    return token;
  }, [fetchMe]);

  const refreshUser = useCallback(async () => {
    if (!accessToken) return;
    try {
      await fetchMe(accessToken);
      return;
    } catch (err: any) {
      // Lỗi tạm thời (mạng, 5xx) thì giữ user cũ, lần poll sau thử lại.
      if (err?.status !== 401) return;
    }
    // Access token hết hạn: lấy token mới bằng refresh cookie rồi đọc lại.
    // Nếu không làm bước này, số point trên UI sẽ đứng im cho tới khi tải lại trang.
    try {
      const token = await refresh();
      await fetchMe(token);
    } catch {
      /* refresh cookie cũng hết hiệu lực — giữ user cũ, thao tác tiếp theo sẽ báo lỗi rõ */
    }
  }, [accessToken, fetchMe, refresh]);

  const login = async (email: string, password: string) => {
    const data = await apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    const token: string | undefined = data?.accessToken;
    if (!token) throw new Error("Missing accessToken from /auth/login");

    setAccessToken(token);

    // Backend login trả user sẵn
    if (data?.user) setUser(data.user as User);
    else await fetchMe(token);
  };

  const register = async (email: string, userName: string, password: string) => {
    await apiFetch("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, user_name: userName, password }),
    });
  };

  const logout = async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  };

  // Auto refresh on first load (if refresh_token cookie exists)
  useEffect(() => {
    (async () => {
      try {
        await refresh();
      } catch {
        setAccessToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Làm mới access token trước khi hết hạn, để request thông thường không phải
  // đụng 401 rồi mới thử lại.
  useEffect(() => {
    if (!accessToken) return;
    const expiresAt = readTokenExpiry(accessToken);
    if (!expiresAt) return;
    const delay = Math.max(5_000, expiresAt - Date.now() - 60_000);
    const id = window.setTimeout(() => {
      void refresh().catch(() => {
        /* refresh cookie hết hiệu lực — để request kế tiếp báo lỗi rõ */
      });
    }, delay);
    return () => window.clearTimeout(id);
  }, [accessToken, refresh]);

  const authFetch = async (path: string, init?: RequestInit) => {
    const doFetch = async (token?: string) => {
      const res = await fetch(joinUrl(path), {
        ...init,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(init?.headers || {}),
        },
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err: any = new Error(data?.message || `HTTP ${res.status}`);
        err.status = res.status;
        err.code = data?.code;
        err.data = data;
        throw err;
      }
      return data;
    };

    try {
      return await doFetch(accessToken || undefined);
    } catch (err: any) {
      if (err?.status === 401) {
        const token = await refresh();
        return await doFetch(token);
      }
      throw err;
    }
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isLoading,
      login,
      register,
      logout,
      refresh,
      refreshUser,
      setUser,
      authFetch,
    }),
    [user, accessToken, isLoading, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
